import * as React from 'react'

import { cn } from '@/lib/utils'
import i18n, { getCurrentDirection } from '@/i18n/config'

// ---------------------------------------------------------------------------
// Direction Context
// ---------------------------------------------------------------------------

type Dir = 'ltr' | 'rtl'

const DirectionContext = React.createContext<Dir | undefined>(undefined)

// ---------------------------------------------------------------------------
// useDirection
// ---------------------------------------------------------------------------

/**
 * Returns the nearest Direction context value, falling back to the global
 * i18n direction when no provider is present. The fallback is reactive to
 * language changes via i18next's `languageChanged` event.
 */
function useDirection(): Dir {
  const ctx = React.useContext(DirectionContext)
  const [, rerender] = React.useReducer((x: number) => x + 1, 0)

  React.useEffect(() => {
    if (ctx !== undefined) return
    i18n.on('languageChanged', rerender)
    return () => {
      i18n.off('languageChanged', rerender)
    }
  }, [ctx])

  if (ctx !== undefined) return ctx
  return getCurrentDirection()
}

// ---------------------------------------------------------------------------
// IntuiDirectionProvider
// ---------------------------------------------------------------------------

export interface IntuiDirectionProviderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** The text direction for this subtree */
  dir: Dir
}

const IntuiDirectionProvider = React.forwardRef<HTMLDivElement, IntuiDirectionProviderProps>(
  ({ dir, className, children, ...props }, ref) => {
    return (
      <DirectionContext.Provider value={dir}>
        <div
          ref={ref}
          dir={dir}
          className={cn('direction-root', className)}
          {...props}
        >
          {children}
        </div>
      </DirectionContext.Provider>
    )
  }
)
IntuiDirectionProvider.displayName = 'IntuiDirectionProvider'

export { IntuiDirectionProvider, useDirection }
export type { Dir }
